/**
 * Native Interactive Site Map (ISM) Feature Entry Point.
 *
 * Responsibilities:
 * - Inject Redux slices into the store
 * - Create and bootstrap the plugin host
 * - Manage map library loading and plugin lifecycle
 * - Export Redux actions and selectors for use in components
 */

import { epic$, store, store$ } from '@redux/store';
import { ReplaySubject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import LoadingStatus from 'enums/loadingStatus';
import { selectLoading, selectConfigurationResourceDomain } from 'layout/redux/selectors';
import createMapLibraryPlugin from 'libraries/mapLibrary.plugin';
import createPluginHost from './plugins/app';
import { createNativeInteractiveSiteMapDataSlice, DATA_SLICE_KEY } from './redux/dataSlice';
import { createMapLibraryLifecycleHandlers, getMapSourceUrl } from './handlers/mapLibraryLifecycleHandlers';
import createMapPlugin from './plugins/map.plugin';

export default function setupNativeInteractiveSiteMap({ communityId, latitude, longitude } = {}) {
  const appRoot = document.querySelector('[data-native-interactive-site-map]');

  if (!appRoot) {
    throw new Error('[Interactive Site Map] Missing [data-native-interactive-site-map] container.');
  }

  // A ReplaySubject(1) that emits once when runtime configuration becomes available.
  const configuration$ = new ReplaySubject(1);

  store$.pipe(
    filter(state => selectLoading(state) === LoadingStatus.Succeeded.toString()),
    map(state => ({
      resourceDomain: selectConfigurationResourceDomain(state),
    })),
    take(1),
  ).subscribe(configuration$);

  const mapSourceElement = document.querySelector('[data-maps-script]');
  const mapSource = getMapSourceUrl(mapSourceElement);
  const dataSlice = createNativeInteractiveSiteMapDataSlice();

  const MapLibraryLifecycle = createMapLibraryLifecycleHandlers(
    store.dispatch.bind(store),
    dataSlice.actions
  );

  const host = createPluginHost({
    root: appRoot,
    services: {
      epic$,
      store$,
      dispatch: store.dispatch.bind(store),
      getState: store.getState.bind(store),
      configuration$,
    },
    state: {
      community: { communityId, latitude, longitude },
    },
    plugins: [
      // Map library must be first: it loads Google Maps and injects context.services.mapFacade.
      createMapLibraryPlugin({
        provider: 'google',
        config: {
          src: mapSource,
          onScriptInjected: MapLibraryLifecycle.onMapLibraryFetch,
          onLoad: MapLibraryLifecycle.onMapLibraryLoaded,
          onError: MapLibraryLifecycle.onMapLibraryError,
        },
      }),
      createMapPlugin(),
    ],
  });

  // Inject Redux slices
  store.injectReducer(DATA_SLICE_KEY, dataSlice.reducer);

  (async () => {
    await host.init();
    await host.start();
  })();

  window.addEventListener('beforeunload', () => {
    host.stop();
  });
}
